import { useState } from "react"
import { Trans } from "react-i18next"
import styled from "styled-components"

import { Banner, PinInput, spacing, Text } from "@minuthq/meatball-ui-react"
import { differenceInMinutes } from "date-fns"

import { useGetUser } from "src/data/user/hooks/useGetUser"
import {
  usePostRequestUserVerificationCode,
  usePostVerifyEmailCode,
} from "src/data/user/queries/userQueries"
import { useEffectOnce } from "src/hooks/useEffectOnce"
import { useTimer } from "src/hooks/useTimer"
import { useTranslate } from "src/i18n/useTranslate"
import { MButton } from "src/ui/Button/MButton"

export function StepVerifyEmail({ onVerified }: { onVerified: () => void }) {
  const { t, langKeys } = useTranslate()
  const user = useGetUser()

  const [code, setCode] = useState("")

  const { timeLeft, startTimer, isTimerRunning } = useTimer()

  const postRequestUserVerificationCode = usePostRequestUserVerificationCode()
  const postVerifyEmailCode = usePostVerifyEmailCode()

  function handleCompletePinInput() {
    postVerifyEmailCode.mutate(
      {
        path: { user_id: user.user_id },
        body: { code },
      },
      {
        onSuccess: () => {
          onVerified()
        },
        onError: () => {
          setCode("")
        },
      }
    )
  }

  function handleResendCode() {
    postRequestUserVerificationCode.mutate(
      {
        path: { user_id: user.user_id },
        body: { verification_type: "code" },
      },
      {
        onSuccess: () => {
          startTimer(60) // 60 seconds cooldown timer
        },
      }
    )
  }

  /**
   * If it was more than 5 minutes ago that the user receieved a verification email,
   * a new code is requested automatically. This handles the case of existing
   * unverified users, since they don't get the code verification when they sign up,
   * (since email verification is not currently enforced).
   */
  useEffectOnce(() => {
    const lastEmailSentAt = user?.email_verification?.email_sent_at

    if (
      !lastEmailSentAt ||
      differenceInMinutes(new Date(), new Date(lastEmailSentAt)) > 5
    ) {
      handleResendCode()
    }
  })

  const codeIsInvalid = postVerifyEmailCode.error?.status === 400

  return (
    <Wrapper>
      <Text variant="heading2" marginBottom={spacing.L}>
        {t(langKeys.email_code_verification_title)}
      </Text>

      <Text marginBottom={spacing.M}>
        <Trans
          i18nKey={t(langKeys.email_code_verification_description, {
            email: user.email,
          })}
          components={{
            bold: (
              <Text variant="subtitle" as="span">
                {user.email}
              </Text>
            ),
          }}
        />
      </Text>

      <PinWrapper>
        <PinInput
          value={code}
          onChange={(value) => {
            setCode(value)
          }}
          onComplete={handleCompletePinInput}
          error={codeIsInvalid}
          loading={postVerifyEmailCode.isLoading}
        />

        {codeIsInvalid && (
          <div>
            <Text variant="bodyS" color="emergency">
              {t(langKeys.email_code_verification_invalid_code)}
            </Text>
            <Text variant="bodyS" color="emergency">
              {t(langKeys.email_code_verification_invalid_code_suggestions)}
            </Text>
          </div>
        )}
      </PinWrapper>

      <Text variant="subtitle" marginBottom={spacing.XS2}>
        {t(langKeys.email_code_verification_no_email_title)}
      </Text>

      <BottomWrapper>
        <Text color="secondary">
          {t(langKeys.email_code_verification_no_email_description)}
        </Text>

        {(postRequestUserVerificationCode.isError ||
          (postVerifyEmailCode.isError &&
            postVerifyEmailCode.error.status !== 400)) && (
          <Banner type="error" fullWidth>
            {t(langKeys.failed_something_went_wrong)}
          </Banner>
        )}

        <MButton
          variant="minimal"
          onClick={handleResendCode}
          disabled={isTimerRunning}
          loading={postRequestUserVerificationCode.isLoading}
        >
          {t(langKeys.email_code_verification_resend)}{" "}
          {isTimerRunning && `(${timeLeft})`}
        </MButton>
      </BottomWrapper>
    </Wrapper>
  )
}

const PinWrapper = styled.div`
  margin-bottom: ${spacing.XL2};
`

const Wrapper = styled.div`
  margin-top: ${spacing.L};
`

const BottomWrapper = styled.div`
  display: grid;
  justify-items: start;
  gap: ${spacing.XL};
`
